import { useContext, useMemo } from 'react';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import { UscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { usePortalUserItems } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisPortal';
import type { ExternalLayerAccessType } from 'hooks/externalLayer/types';

/** The only ArcGIS portal item titles offered in the layer dropdown. */
export const HAZARD_LAYER_TITLES = [
  'Hurricanes',
  'Realtime Wildfires',
] as const;

/**
 * Server-side ArcGIS portal search; results are re-filtered by exact title with
 * `isHazardLayerTitle`.
 */
export const HAZARD_PORTAL_SEARCH_TERM = 'hurricane OR wildfire';

export type HazardLayerOption = {
  value: string;
  label: string;
  kind: 'portal';
  arcgisItemId?: string;
  serviceUrl?: string;
  authType?: ExternalLayerAccessType;
};

type PortalHazardItem = {
  id?: string | null;
  title?: string | null;
  url?: string | null;
  authType?: ExternalLayerAccessType;
};

export const isHazardLayerTitle = (title: string): boolean =>
  HAZARD_LAYER_TITLES.some(
    (hazardTitle) => hazardTitle.toLowerCase() === title.trim().toLowerCase()
  );

export const buildHazardLayerOptions = ({
  portalItems = [],
}: {
  portalItems?: PortalHazardItem[];
}): HazardLayerOption[] => {
  const seenIds = new Set<string>();
  const optionByTitle = new Map<string, HazardLayerOption>();

  portalItems.forEach((item) => {
    if (!item.id || !item.title || seenIds.has(item.id)) return;
    seenIds.add(item.id);
    if (!isHazardLayerTitle(item.title)) return;
    const title = item.title.trim();
    if (optionByTitle.has(title.toLowerCase())) return;
    optionByTitle.set(title.toLowerCase(), {
      value: `portal:${item.id}`,
      label: title,
      kind: 'portal',
      arcgisItemId: item.id,
      serviceUrl: item.url ?? undefined,
      authType: item.authType ?? 'userAuth',
    });
  });

  // Fixed order regardless of portal result order.
  return HAZARD_LAYER_TITLES.flatMap((title) => {
    const option = optionByTitle.get(title.toLowerCase());
    return option ? [option] : [];
  });
};

export const useHazardLayerOptions = () => {
  const group = useCurrentGroup();

  // Read the context directly (not `useUscgMapContext`, which throws) so the
  // hook still works when no map provider is mounted.
  const isArcGISAuthorized = useContext(UscgMapContext)?.isArcGISAuthorized;

  const { data: portalData, isLoading } = usePortalUserItems({
    portalState: {
      esriItem: 'Layers',
      owner: 'Esri',
      sort: 'num-views',
      searchTerm: HAZARD_PORTAL_SEARCH_TERM,
    },
    group,
    enabled: !!isArcGISAuthorized,
  });

  const options = useMemo(
    () => buildHazardLayerOptions({ portalItems: portalData?.items ?? [] }),
    [portalData]
  );

  return { options, isLoading };
};
